import Api from "../../../services/api";
import { DriveStore } from "../../../store/reducers";
import { handleApi } from "../../../utils/error";
import { IInspectionAsset } from "../store/types";
import { Device } from "../../../model/Device";
import { getAssetTypeFromDevice } from "../utils/assetFormatters";
import { getCurrentPosition, getLastPosition } from "../../../utils/location";
import { getCurrentVehicle } from "../../../utils/device";
import { getServerDate } from "../../../utils/storage/date";
import LatLon from "../../../utils/latlon";
import constants from "../../../constants";

/** Max assets listed, for both the nearby (GPS) list and keyword search; a UI list size, not a server limit. */
const MAX_SEARCH_RESULTS = 20;

/** A fix this recent is reused, so repeated filter searches don't each wait on GPS. */
const GPS_CACHE_TTL_MS = 30000;

/** Cached GPS position with timestamp for TTL checking */
let cachedPosition: { position: GeolocationPosition; timestamp: number } | null = null;

/** Cached assigned asset. undefined = never fetched, null = fetched with no assignment. */
let cachedAssignedAsset: IInspectionAsset | null | undefined = undefined;

/**
 * Get GPS position from cache if fresh, otherwise fetch and cache.
 * Falls back to last known position if current position fails.
 * @param store - Redux store for location utilities
 * @returns Cached or fresh position, or undefined if unavailable
 */
async function getCachedPosition(store: DriveStore): Promise<GeolocationPosition | undefined> {
    const now = Date.now();
    if (cachedPosition && now - cachedPosition.timestamp < GPS_CACHE_TTL_MS) {
        return cachedPosition.position;
    }

    // Distance only sorts the list, so after 5 s a last-known position is good enough.
    const position = await getCurrentPosition(store, { timeout: 5000 })
        .catch(() => getLastPosition(store))
        .catch(() => undefined);

    if (position) {
        cachedPosition = { position, timestamp: now };
    }

    return position;
}

/** Options for findNearbyAssets */
export interface IFindNearbyAssetsOptions {
    /** Skip GPS lookup entirely (for text searches where distance isn't needed) */
    skipGps?: boolean;
    /** Let the proximity search return archived assets ("Show archived assets"); off by default (MYG-151991) */
    includeArchived?: boolean;
}

/**
 * Clear the GPS position cache. Exported for testing purposes only.
 * @internal
 */
export function clearGpsCache(): void {
    cachedPosition = null;
}

/** Returns the last-fetched assigned asset, or undefined if findAssignedAsset has never been called. */
export function getCachedAssignedAsset(): IInspectionAsset | null | undefined {
    return cachedAssignedAsset;
}

/** Clears the assigned asset cache. Call on driver switch so the incoming driver's assignment is fetched fresh. */
export function clearAssignedAssetCache(): void {
    cachedAssignedAsset = undefined;
}

/** Sentinel value used by the Drive native layer when no vehicle is assigned to the driver. */
export const NO_DEVICE_ID = "NoDeviceId";

/** Property selector for DeviceStatusInfo - only fetch properties needed for distance calculation */
const DEVICE_STATUS_PROPERTY_SELECTOR = {
    fields: ["device", "latitude", "longitude"],
    isIncluded: true
};

/**
 * DeviceStatusInfo with calculated distance property (in kilometers)
 */
interface IDeviceStatusInfoWithDistance extends IDeviceStatusInfo {
    distance: number;
}

/**
 * Service for finding assets for inspections (vehicles, trailers, equipment): the closest
 * MAX_SEARCH_RESULTS by GPS distance, or the caller's pre-fetched search results.
 */
export class InspectionAssetsService {
    constructor(
        private readonly api: Api,
        private readonly store: DriveStore
    ) {}

    /**
     * Get the driver's currently assigned asset via getCurrentVehicle (native vehicle assignment).
     * Falls back gracefully if no vehicle is assigned or the device cannot be fetched.
     * @param assetTypeMap - Pre-created Map for asset type lookups
     */
    async findAssignedAsset(assetTypeMap: Map<string, string>): Promise<IInspectionAsset | null> {
        try {
            const currentVehicle = getCurrentVehicle(this.store);
            if (!currentVehicle?.id || currentVehicle.id === NO_DEVICE_ID) {
                cachedAssignedAsset = null;
                return null;
            }

            const devices = await this.fetchDevices([currentVehicle.id]);
            const device = devices[0];
            if (!device) {
                cachedAssignedAsset = null;
                return null;
            }

            const assetType = getAssetTypeFromDevice(device, assetTypeMap);
            if (!assetType) {
                cachedAssignedAsset = null;
                return null;
            }

            const result = { device, assetType: assetType.name, assetTypeId: assetType.id };
            cachedAssignedAsset = result;
            return result;
        } catch {
            // Don't update cache on error — preserve the previous value so the
            // caller can still show stale-but-valid data on network failures.
            return null;
        }
    }

    /**
     * Get nearby assets (all types: vehicles, trailers, equipment)
     * @param assetTypeMap - Pre-created Map for asset type lookups
     * @param selectedAsset - Currently selected asset (to exclude from results to avoid duplicates), null if none selected
     * @param devices - Optional pre-fetched devices (for search results - to avoid duplicate API calls)
     * @param groups - Optional group filter (for search results with filters)
     * @param options - Optional settings (e.g., skipGps for text searches)
     */
    async findNearbyAssets(
        assetTypeMap: Map<string, string>,
        selectedAsset: IInspectionAsset | null,
        devices?: Device[],
        groups?: { id: string }[],
        options?: IFindNearbyAssetsOptions
    ): Promise<IInspectionAsset[]> {
        try {
            const hasDevices = devices && devices.length > 0;
            const skipGps = options?.skipGps ?? false;

            // Cached to avoid repeated 5s GPS timeouts on search/filter; undefined if unavailable or skipped.
            const position = skipGps ? undefined : await getCachedPosition(this.store);

            // Text-search fast path: pre-fetched devices, no GPS needed.
            if (hasDevices && skipGps) {
                // NO_DEVICE_ID means no real asset is selected, so there is nothing to exclude.
                const selectedDeviceId = selectedAsset?.device.id;
                const assets: IInspectionAsset[] = [];
                for (const device of devices) {
                    if (selectedDeviceId && selectedDeviceId !== NO_DEVICE_ID && device.id === selectedDeviceId) {
                        continue;
                    }
                    const assetType = getAssetTypeFromDevice(device, assetTypeMap);
                    if (assetType) {
                        assets.push({ device, assetType: assetType.name, assetTypeId: assetType.id });
                    }
                }
                assets.sort((a, b) => a.device.name.localeCompare(b.device.name));
                return assets;
            }

            // Filter search with pre-fetched devices (hasDevices && !skipGps).
            if (hasDevices) {
                const deviceIds = devices.map(d => d.id);
                const statuses = await this.fetchDeviceStatusesByDeviceIds(deviceIds);

                if (position && statuses.length > 0) {
                    this.calculateAndSortByDistance(statuses, position);
                }

                const statusMap = new Map(statuses.map(s => [s.device.id, s as IDeviceStatusInfoWithDistance]));
                const selectedDeviceId = selectedAsset?.device.id;
                const assets: IInspectionAsset[] = [];
                for (const device of devices) {
                    if (selectedDeviceId && selectedDeviceId !== NO_DEVICE_ID && device.id === selectedDeviceId) {
                        continue;
                    }
                    const assetType = getAssetTypeFromDevice(device, assetTypeMap);
                    if (!assetType) { continue; }
                    const status = statusMap.get(device.id);
                    assets.push({ device, assetType: assetType.name, assetTypeId: assetType.id, distance: status?.distance });
                }
                // GPS-tracked assets (with distance) sort first; GPS-less assets follow
                assets.sort((a, b) => {
                    const hasA = a.distance !== undefined;
                    const hasB = b.distance !== undefined;
                    if (hasA && hasB) { return (a.distance as number) - (b.distance as number); }
                    if (hasA) { return -1; }
                    if (hasB) { return 1; }
                    return 0;
                });
                return assets;
            }

            // Proximity search: no pre-fetched devices. Without GPS there is nothing to show under "Assets Nearby".
            if (!position) {
                return [];
            }

            const statuses = await this.fetchDeviceStatusesByProximity(position, groups, options?.includeArchived);

            if (statuses.length === 0) {
                return [];
            }

            const selectedDeviceId = selectedAsset?.device.id;
            const filteredStatuses = (selectedDeviceId && selectedDeviceId !== NO_DEVICE_ID)
                ? statuses.filter(s => s.device.id !== selectedDeviceId)
                : statuses;

            this.calculateAndSortByDistance(filteredStatuses, position);

            const filteredDeviceIds = filteredStatuses.map(s => s.device.id);
            const fetchedDevices = await this.fetchDevices(filteredDeviceIds);

            return this.formatAssets(fetchedDevices, filteredStatuses, assetTypeMap);
        } catch (err) {
            handleApi(err);
            return [];
        }
    }

    /**
     * Calculate distances using Haversine formula and sort by distance
     * Mutates the status objects by adding distance property
     */
    private calculateAndSortByDistance(statuses: IDeviceStatusInfo[], position: GeolocationPosition): void {
        const currentCoord = new LatLon(position.coords.latitude, position.coords.longitude);

        statuses.forEach(status => {
            (status as IDeviceStatusInfoWithDistance).distance = currentCoord.distanceTo(
                new LatLon(status.latitude, status.longitude)
            );
        });

        statuses.sort((a, b) => {
            const statusA = a as IDeviceStatusInfoWithDistance;
            const statusB = b as IDeviceStatusInfoWithDistance;
            // Not covered by unit tests: distance is always assigned just above, so the ?? 0 fallbacks are dead. This code requires rework the next time it is changed.
            return (statusA.distance ?? 0) - (statusB.distance ?? 0);
        });
    }

    /**
     * Format statuses and devices into IInspectionAsset array
     */
    private formatAssets(
        devices: Device[],
        statuses: IDeviceStatusInfo[],
        assetTypeMap: Map<string, string>
    ): IInspectionAsset[] {
        const deviceMap = new Map(devices.map(d => [d.id, d]));

        // Iterate statuses, not devices, to keep the distance order.
        const assets: IInspectionAsset[] = [];
        for (const status of statuses) {
            const device = deviceMap.get(status.device.id);
            if (device) {
                const assetType = getAssetTypeFromDevice(device, assetTypeMap);
                if (assetType) {
                    const statusWithDistance = status as IDeviceStatusInfoWithDistance;
                    assets.push({
                        device,
                        assetType: assetType.name,
                        assetTypeId: assetType.id,
                        distance: statusWithDistance.distance
                    });
                }
            }
        }

        return assets;
    }

    /**
     * Fetch device statuses by proximity (for "Assets Nearby")
     * Returns closest assets to the user's position. DeviceStatusInfo searches include archived
     * devices unless the nested deviceSearch carries a fromDate, so one is sent unless the driver
     * opted into archived assets (MYG-151991).
     */
    private async fetchDeviceStatusesByProximity(
        position: GeolocationPosition,
        groups?: { id: string }[],
        includeArchived = false
    ): Promise<IDeviceStatusInfo[]> {
        const statuses = await this.api.callPromise("Get", {
            typeName: "DeviceStatusInfo",
            search: {
                closestAssetLimit: MAX_SEARCH_RESULTS,
                position: {
                    x: position.coords.longitude,
                    y: position.coords.latitude
                },
                deviceSearch: {
                    groups: groups || [{ id: constants.GROUPS.ASSET_GROUP_ID }],
                    fromDate: includeArchived ? undefined : getServerDate().toISOString()
                }
            },
            propertySelector: DEVICE_STATUS_PROPERTY_SELECTOR
        });

        return statuses || [];
    }

    /**
     * Fetch device statuses by device IDs (for "Search")
     * Returns statuses for specific devices
     */
    private async fetchDeviceStatusesByDeviceIds(deviceIds: string[]): Promise<IDeviceStatusInfo[]> {
        const statuses = await this.api.callPromise("Get", {
            typeName: "DeviceStatusInfo",
            search: {
                deviceSearch: { deviceIds }
            },
            propertySelector: DEVICE_STATUS_PROPERTY_SELECTOR
        });

        return statuses || [];
    }

    /**
     * Fetch device details by IDs
     */
    private async fetchDevices(deviceIds: string[]): Promise<Device[]> {
        if (deviceIds.length === 0) {
            return [];
        }

        const results = await this.api.callPromise("Get", {
            typeName: "Device",
            search: { deviceIds }
        });

        return results ?? [];
    }
}
